// Client i18n layer for the TDEE calculator SPA.
//
// Locale is derived from the URL PATH prefix, not navigator.language: the app
// is served at /tdee/ (English) and /tdee/es/ (Spanish), and the worker
// prerenders the matching SEO shell. detectLang() reads the path so the SPA
// runs in the same language the crawler saw. The resolved locale is threaded
// through every user-visible component via getStrings(lang) and is sent to the
// server on /api/chat + /api/plan/* as `lang`, so the interview, the plan, and
// every deterministic server string are localized too.
//
// English values in en.ts are byte-identical to the pre-i18n literals — the
// English experience must not change. Only rich JSX blocks (a few hero /
// CTA / methodology fragments with <em>/<strong>) stay inline in components,
// branched on lang, so English markup is preserved exactly.

import { en } from './en'
import { es } from './es'
import type { Strings } from './types'

export type Lang = 'en' | 'es'
export type { Strings }

const DICTS: Record<Lang, Strings> = { en, es }

/** Resolve the string dictionary for a locale (defaults to English). */
export function getStrings(lang: Lang): Strings {
  return DICTS[lang] ?? en
}

/**
 * Locale from a pathname. 'es' when the path has an `es` segment
 * (/tdee/es, /tdee/es/, or the dev-direct /es); English otherwise. Matching a
 * full path segment avoids false hits on words that merely contain "es".
 */
export function detectLang(pathname: string): Lang {
  return /(^|\/)es(\/|$)/.test(pathname) ? 'es' : 'en'
}

/** Current locale from the browser path. SSR-safe (returns 'en' with no window). */
export function useLang(): Lang {
  if (typeof window === 'undefined') return 'en'
  return detectLang(window.location.pathname)
}

/** BCP-47 locale tag for Intl formatting (dates, numbers). */
export function intlLocale(lang: Lang): string {
  return lang === 'es' ? 'es' : 'en-US'
}
